import {getTestProps} from '@benchling/js-test-utils';
import {naturalSort, naturalSortByKey} from '@benchling/js-utils';
import {css, StyleSheet} from 'aphrodite';
import classNames from 'classnames';
import React, {useState} from 'react';

import {getSpreadsheetIndexOffset, SpreadsheetType} from 'bulk-import/panes/ValidationErrorsTable';
import {
  ERRORS_TABLE_PAGINATION_LIMIT,
  StudyWarningSpreadsheetValidationError,
} from 'bulk-import/SpreadsheetBulkImportConstants';
import ResultsPager from 'ui-library/ResultsPager';
import {Cell, Column, Table} from 'ui-library/simple-table/SimpleTable';

export default function StudyWarningsTable({
  spreadsheetType,
  studyWarnings,
}: {
  spreadsheetType: SpreadsheetType;
  studyWarnings: Array<StudyWarningSpreadsheetValidationError>;
}): JSX.Element | null {
  // Pagination offset
  const [pageOffset, setOffset] = useState<number>(0);

  const rowsWithStudyWarningsCount = studyWarnings.length;
  if (rowsWithStudyWarningsCount === 0) {
    return null;
  }

  const sampleHashToRowNumbers = new Map<string, Array<string>>();
  const sampleHashToErrorMessage = new Map<string, string>();
  for (const warning of studyWarnings) {
    // Set up the mapping from sample hash to error message - all the rows with the same sample hash will
    // have the same error message
    const sampleHash = warning.sampleHash;
    const errorMessage = warning.errorMessage;
    sampleHashToErrorMessage.set(sampleHash, errorMessage);

    const rowNumber = warning.row + getSpreadsheetIndexOffset(spreadsheetType);
    if (sampleHashToRowNumbers.get(sampleHash) != null) {
      sampleHashToRowNumbers.get(sampleHash)!.push(rowNumber.toString());
    } else {
      sampleHashToRowNumbers.set(sampleHash, [rowNumber.toString()]);
    }
  }

  let studyWarningTableRows: Array<{rows: string; errorMessage: string}> = [];
  for (const [sampleHash, rowNumbers] of sampleHashToRowNumbers) {
    // Sort the rowNumbers for each sample hash
    const rows = naturalSort(rowNumbers).join(', ');
    const errorMessage = sampleHashToErrorMessage.get(sampleHash) ?? '';
    studyWarningTableRows.push({rows, errorMessage});
  }
  // Sort by the first column (Row #)
  studyWarningTableRows = naturalSortByKey(studyWarningTableRows, 'rows');

  return (
    <div className="u-vertical-stretch" {...getTestProps({component: 'StudyWarningsTable', element: 'root'})}>
      <div
        {...getTestProps({
          component: 'StudyWarningsTable',
          element: 'headerMessage',
        })}
        className={classNames('u-small-spacing-top', css(styles.headerMessage))}
      >
        The following rows don’t align with a sample definition in the study you specified. Discrepancy tasks
        will be created for each mismatched sample once the import completes.
      </div>
      <Table
        className={classNames(
          'bulkImportModal-table-body u-small-spacing-top u-no-spacing-bottom',
          css(styles.table),
          // Set the height for all pages to be fixed if we have pagination to avoid the pagination controls
          // jumping at the last page which may have less items
          studyWarningTableRows.length > ERRORS_TABLE_PAGINATION_LIMIT ? css(styles.tableFullHeight) : null
        )}
        numRows={Math.min(studyWarningTableRows.length - pageOffset, ERRORS_TABLE_PAGINATION_LIMIT)}
        setOffset={setOffset}
        // The following props make the table scrollable with a fixed header and a max-height
        isCompact={true}
        fitContent={true}
        frozenHeader={true}
        overflowVisible={true}
        showBorder={false}
        tableLayout="fixed"
      >
        <Column
          header={<Cell className={css(styles.rowNumberCell)}>Row #</Cell>}
          renderCell={(rowIndex) => (
            <Cell className={css(styles.rowNumberCell)}>
              <div className={css(styles.rowNumberCellContainer)}>
                {studyWarningTableRows[pageOffset + rowIndex].rows}
              </div>
            </Cell>
          )}
        />
        <Column
          header={<Cell className={css(styles.messageCell)}>Message</Cell>}
          renderCell={(rowIndex) => (
            <Cell className={css(styles.messageCell)}>
              {studyWarningTableRows[pageOffset + rowIndex].errorMessage}
            </Cell>
          )}
        />
      </Table>
      {
        // Show pagination only if there are more than ERRORS_TABLE_PAGINATION_LIMIT errors
        studyWarningTableRows.length > ERRORS_TABLE_PAGINATION_LIMIT && (
          <ResultsPager
            setOffset={setOffset}
            pagination={{
              offset: pageOffset,
              total: studyWarningTableRows.length,
              isExact: true,
              limit: ERRORS_TABLE_PAGINATION_LIMIT,
            }}
            isLoading={false}
          />
        )
      }
    </div>
  );
}

const styles = StyleSheet.create({
  rowNumberCell: {
    width: '12% !important',
    whiteSpace: 'pre-wrap',
  },
  rowNumberCellContainer: {
    maxHeight: '10vh !important',
    overflow: 'scroll',
  },
  messageCell: {
    width: '88% !important',
    whiteSpace: 'pre-wrap',
  },
  table: {
    display: 'flex',
    flexDirection: 'column',
    maxHeight: '40vh',
  },
  tableFullHeight: {
    height: '40vh',
  },
  headerMessage: {
    fontSize: '13px',
  },
});
